import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Infinity, ShieldCheck, Users, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import "./ServicesScene.css";

const principles = [
  { label: "Strategic advisory", icon: Users },
  { label: "Fast execution", icon: Zap },
  { label: "Long-term partnerships", icon: Infinity },
];

export default function ServicesScene() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => document.body.classList.toggle("home-services-active", entry.isIntersecting),
      { threshold: 0.35 },
    );
    observer.observe(section);
    return () => {
      observer.disconnect();
      document.body.classList.remove("home-services-active");
    };
  }, []);

  return (
    <section ref={sectionRef} id="services" data-testid="services-scene" className="edx-services-scene" aria-labelledby="services-heading">
      <div className="edx-services-art" aria-hidden="true">
        <img src="/edenox-services-network-v2.png" alt="" width="1672" height="941" loading="lazy" />
        <svg className="edx-services-live-flow" viewBox="0 0 1672 941" preserveAspectRatio="xMaxYMid meet">
          <defs>
            <filter id="services-flow-soft" x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur stdDeviation="6" />
            </filter>
            <mask id="services-flow-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="1672" height="941">
              <rect width="1672" height="941" fill="black" />
              <g className="edx-services-flow-routes" filter="url(#services-flow-soft)">
                <path d="M858 435 H814 V376" pathLength="100" />
                <path d="M1087 402 V374" pathLength="100" />
                <path d="M1329 435 H1393 V375" pathLength="100" />
                <path d="M858 463 H810 V509" pathLength="100" />
                <path d="M1087 491 V510" pathLength="100" />
                <path d="M1329 463 H1394 V509" pathLength="100" />
                <path d="M941 235 H964" pathLength="100" />
                <path d="M1220 235 H1242" pathLength="100" />
                <path d="M941 618 H963" pathLength="100" />
                <path d="M1220 618 H1242" pathLength="100" />
              </g>
            </mask>
          </defs>
          <image className="edx-services-flow-texture" href="/edenox-services-network-v2.png" width="1672" height="941" preserveAspectRatio="xMaxYMid meet" mask="url(#services-flow-mask)" />
        </svg>
      </div>
      <div className="edx-services-shade" aria-hidden="true" />
      <div className="edx-services-ambient" aria-hidden="true" />

      <div className="edx-services-frame">
        <div className="edx-services-content">
          <div className="edx-services-eyebrow">
            <span>OUR SERVICES</span><i /><b>IDEAS. SYSTEMS. REAL IMPACT.</b>
          </div>

          <motion.h2 id="services-heading" className="edx-services-title" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.55 }} transition={{ duration: 0.65 }}>
            <span>We build what</span>
            <span className="edx-services-gradient">moves tomorrow.</span>
          </motion.h2>

          <p className="edx-services-intro">
            EDENOX designs and builds software, AI systems, digital products, cloud infrastructure,
            and data automation for forward-thinking businesses around the world.
          </p>

          <div className="edx-services-actions">
            <Link to="/consultation" className="edx-services-primary">Book a discovery call <ArrowUpRight aria-hidden="true" /></Link>
            <Link to="/capabilities" className="edx-services-secondary">See all services <ArrowRight aria-hidden="true" /></Link>
          </div>

          <div className="edx-services-principles">
            {principles.map(({ label, icon: Icon }) => <span key={label}><Icon aria-hidden="true" />{label}</span>)}
          </div>

          <div className="edx-services-signoff" aria-hidden="true"><i /><span>BUILT FOR A BRIGHTER TOMORROW.</span></div>
        </div>

        <div className="edx-services-side-note" aria-hidden="true">
          <ShieldCheck /><span>REAL SOLUTIONS<br />FOR A BRIGHTER<br />TOMORROW</span>
        </div>
      </div>
    </section>
  );
}
